import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Mail, Send, Eye } from 'lucide-react';
import { EmailPreviewDialog } from '@/components/EmailPreviewDialog';

/**
 * Dialog per inviare via email il PDF di un contratto (compilato o firmato).
 * props: open, onOpenChange, contract (documento contratto completo), onSent?
 */
export function SendContractEmailDialog({ open, onOpenChange, contract, onSent }) {
  const [email, setEmail] = useState('');
  const [mode, setMode] = useState('filled');
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isSigned = !!contract?.signature?.customer_signature_data_url;

  useEffect(() => {
    if (open && contract) {
      setEmail(contract?.customer?.email || '');
      setMode(isSigned ? 'signed' : 'filled');
    }
    // eslint-disable-next-line
  }, [open, contract?.id]);

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await api.previewEmail({ type: 'contratto', contract_id: contract.id, company_id: contract.company_id });
      setPreviewData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento anteprima');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }
    setSending(true);
    try {
      const res = await api.sendContractEmail(contract.id, { to_email: email, mode });
      toast.success(`Contratto inviato a ${res.to}`);
      onOpenChange?.(false);
      onSent?.();
    } catch (e) {
      toast.error('Invio email non riuscito', {
        description: e?.response?.data?.detail || 'Il sistema ha riprovato automaticamente senza successo. Riprova tra qualche minuto.',
        duration: 7000,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="contract-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="size-4 text-primary" />Invia contratto via email</DialogTitle>
            <DialogDescription>
              {contract?.contract?.number ? `Contratto ${contract.contract.number}` : 'Il PDF del contratto'} verrà inviato in allegato con il branding aziendale.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Email destinatario</Label>
              <Input
                type="email"
                placeholder="email.cliente@esempio.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="contract-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Versione del contratto</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="contract-email-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="filled">Compilato (senza firma)</SelectItem>
                  <SelectItem value="signed" disabled={!isSigned}>Firmato{!isSigned ? ' — non disponibile' : ''}</SelectItem>
                </SelectContent>
              </Select>
              {!isSigned && <p className="text-[11px] text-muted-foreground">Il contratto non è ancora stato firmato dal cliente.</p>}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={handlePreview} disabled={previewLoading} data-testid="contract-email-preview-button">
              {previewLoading ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Eye className="size-4 mr-2" />}
              Anteprima
            </Button>
            <Button onClick={handleSend} disabled={sending || !email} data-testid="contract-email-send-button">
              {sending ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Send className="size-4 mr-2" />}
              Invia email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <EmailPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} preview={previewData} loading={previewLoading} />
    </>
  );
}
